/**
 * Ship-date helpers for Lane planner ↔ cargo.one.
 *
 * cargo.one validates dropoffDate in UTC ("must be in the future"). A local
 * "today" in US evenings is often already yesterday/today-past in UTC, which
 * is why same-day quotes and re-run saved searches fail. These helpers clamp
 * to the earliest date/time cargo.one will accept.
 */
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const DATE_ONLY = 'YYYY-MM-DD';

/**
 * Ocean dropoffDate is a date-only FutureDate (strictly after UTC today).
 * Earliest acceptable calendar day = tomorrow UTC.
 */
export const earliestOceanDropoffDate = (now: Dayjs = dayjs()): string =>
  dayjs.utc(now.toDate()).add(1, 'day').format(DATE_ONLY);

/** Clamp a selected ocean ship date to the earliest cargo.one accepts. */
export const resolveOceanDropoffDate = (
  selected: string | Dayjs | null | undefined,
  now: Dayjs = dayjs()
): string => {
  const earliest = earliestOceanDropoffDate(now);
  if (selected == null || selected === '') return earliest;
  // String payloads (saved searches) are UTC; picker Dayjs values stay local.
  const day =
    typeof selected === 'string' ? dayjs.utc(selected) : dayjs(selected);
  if (!day.isValid()) return earliest;
  const formatted = day.format(DATE_ONLY);
  return formatted < earliest ? earliest : formatted;
};

/**
 * Air dropoffDate is a datetime that must be strictly after now.
 * Prefer noon UTC on the selected calendar day; bump to the next future
 * noon UTC when that instant is already past.
 */
export const resolveAirDropoffDateTime = (
  selected: string | Dayjs | null | undefined,
  now: Dayjs = dayjs()
): string => {
  const nowUtc = dayjs.utc(now.toDate());
  const noonOn = (day: Dayjs): Dayjs =>
    dayjs.utc(day.format(DATE_ONLY) + 'T12:00:00Z');

  let candidate: Dayjs;
  if (selected == null || selected === '') {
    candidate = noonOn(nowUtc);
  } else {
    // String payloads (saved searches) are UTC; picker Dayjs values stay local.
    const day =
      typeof selected === 'string' ? dayjs.utc(selected) : dayjs(selected);
    if (!day.isValid()) {
      candidate = noonOn(nowUtc);
    } else {
      candidate = noonOn(day);
    }
  }

  if (!candidate.isAfter(nowUtc)) {
    const noonToday = noonOn(nowUtc);
    candidate = noonToday.isAfter(nowUtc)
      ? noonToday
      : noonOn(nowUtc.add(1, 'day'));
  }
  return candidate.format('YYYY-MM-DDTHH:mm:ss[Z]');
};

/**
 * Calendar day for the air ship-date picker.
 * Uses the resolved UTC date (cargo.one noon-UTC day), clamped so it is never
 * before the viewer's local today — converting the UTC instant to local can
 * land on tomorrow in UTC+ and wrongly disable an acceptable day.
 */
export const airDropoffPickerDay = (
  selected: string | Dayjs | null | undefined,
  now: Dayjs = dayjs()
): Dayjs => {
  const resolvedUtc = dayjs.utc(resolveAirDropoffDateTime(selected, now));
  const utcDate = resolvedUtc.format(DATE_ONLY);
  const localToday = now.format(DATE_ONLY);
  return dayjs(utcDate < localToday ? localToday : utcDate);
};

/** Earliest selectable air ship day in the date picker. */
export const earliestAirDropoffDay = (now: Dayjs = dayjs()): Dayjs =>
  airDropoffPickerDay(null, now);
